import React, { useState, useRef, useEffect } from 'react';
import TopBar from '../Tools/Topbar';
import LeftSide from '../Tools/LeftSidebar';
import AvailableAssets from '../Chart/Overview/AvailableAssets';
import AvailableSpecialties from '../Chart/Overview/AvailableSpecialties';
import ResourcesByType from '../Chart/Overview/ResourcesByType';
import EmployeeCapacityUsage from '../Chart/Overview/EmployeeCapacityUsage';
import AssetCapacityUsage from '../Chart/Overview/AssetCapacityUsage';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import api from '../../../api';

const DashboardBody = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [cases, setCases] = useState([]);
  const [caseData, setCaseData] = useState({
    total_case_count: 0,
    accepted_case_count: 0,
    rejected_case_count: 0,
    active_case_count: 0
  });

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  useEffect(() => {
    api.get('/case-capacity')
      .then((res) => {
        setCaseData(res.data);
      })
      .catch((err) => {
        console.error("Failed to load case data", err);
      });
  }, []);

  useEffect(() => {
    api.get('/accepted-cases-overview')
      .then((response) => {
        setCases(response.data);
      })
      .catch((error) => {
        console.error('Error fetching accepted cases:', error);
      });
  }, []);

  return (
    <div id="main-wrapper">
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            <h5 className="card-title fw-semibold mb-0 lh-sm">Case Capacity</h5>
            <p className="card-subtitle mb-0 mt-1">
              Visualize how many cases of each type can currently be supported based on available resources.
            </p>

            <div className="row mt-4">
              <div className="col-lg-4 my-2">
                <div className="card border border-primary p-4 zoom-in bg-primary-subtle shadow-none">
                  <div className="card-display">
                    <div className="left-side">
                      <h5>Accepted Cases</h5>
                      <h2 className="text-primary fw-bolder fs-12">{caseData.accepted_case_count}</h2>
                      <p className="mb-0">From total {caseData.total_case_count} submitted cases</p>
                    </div>
                    <div className="icon fs-12">
                      <i className="ti ti-briefcase-2 text-primary"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 my-2">
                <div className="card border border-danger p-4 zoom-in bg-danger-subtle shadow-none">
                  <div className="card-display">
                    <div className="left-side">
                      <h5>Rejected Cases</h5>
                      <h2 className="text-danger fw-bolder fs-12">{caseData.rejected_case_count}</h2>
                      <p className="mb-0">Due to unavailable resources</p>
                    </div>
                    <div className="icon fs-12">
                      <i className="ti ti-playlist-x text-danger"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 my-2">
                <div className="card border border-success p-4 zoom-in bg-success-subtle shadow-none">
                  <div className="card-display">
                    <div className="left-side">
                      <h5>Active Resources</h5>
                      <h2 className="text-success fw-bolder fs-12">{caseData.active_case_count}</h2>
                      <p className="mb-0">Currently assigned to live cases</p>
                    </div>
                    <div className="icon fs-12">
                      <i className="ti ti-users-group text-success"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

             <div className="row mb-5">
              <div className="col-lg-6 my-2">
                <AvailableAssets/>
              </div>
              <div className="col-lg-6 my-2">
                <AvailableSpecialties/>
              </div>
              </div>

            <div className="card w-100 position-relative overflow-hidden">
              <div className="card-body p-4">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Accepted Cases Overview</h5>
                <p className="card-subtitle mb-0 mt-1">
                  Recent cases that were successfully assigned and activated using available resources.
                </p>
                <div className="py-3 px-0">
                  <div className="table-responsive rounded-2 ros-table">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                          <th><h6 className="fw-semibold mb-0">Type</h6></th>
                          <th><h6 className="fw-semibold mb-0">Submission Date</h6></th>
                          <th><h6 className="fw-semibold mb-0">Duration</h6></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cases.length > 0 ? (
                          cases.map((item) => (
                            <tr key={item.id}>
                              <td><p className="mb-0 fw-normal">{item.case_name}</p></td>
                              <td><p className="mb-0 fw-normal">{item.case_type}</p></td>
                              <td><p className="mb-0 fw-normal">{new Date(item.submitted_at).toLocaleDateString()}</p></td>
                              <td><p className="mb-0 fw-normal">{item.time_difference}</p></td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">Loading or no data available.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-lg-6 my-2">
                <ResourcesByType/>
              </div>
              <div className="col-lg-6 my-2">
                <div className="card w-100 mb-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title fw-semibold">Current Capacity Usage</h5>
                    <p className="card-subtitle mb-4">Live snapshot of available vs utilized workforce and assets.</p>
                    <div className="d-flex flex-wrap justify-content-center gap-5">
                      <div className="text-center">
                        <div className="employee-donut-panel" style={{width: '100%', maxWidth: '250px'}}>
                          <EmployeeCapacityUsage />
                        </div>
                        
                      </div>
                      <div className="text-center">
                        <div className="assets-donut-panel" style={{width: '100%', maxWidth: '250px'}}>
                          <AssetCapacityUsage />
                        </div>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
             <div className="row mb-5">
          <div className="col-lg-6 my-2">
            <div className="card mb-0 h-100">
              <div className="card-body">
                <h4 className="card-title">Critical Alarms</h4>
                <p className="card-subtitle mb-4">Most recent critical events</p>

                <ul className="feeds ps-0">
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-danger-subtle text-danger btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-alert-triangle fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Low stock</span>
                          <p className="mb-0">Type A (2 units remaining)</p>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 5 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-danger-subtle text-danger btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-alert-triangle fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Critical dependency failure</span>
                          <p className="mb-0">Database cluster</p>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 12 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-danger-subtle text-danger btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-alert-triangle fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Specialist unavailable</span>
                          <p className="mb-0">Dr. S. Patel</p>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 25 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-danger-subtle text-danger btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-alert-triangle fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">High utilization</span>
                          <p className="mb-0">Server Group X (95% load)</p>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 1 min ago</span>
                      </div>
                    </div>
                  </div>
                </ul>
                <button type="button" className="btn mb-1 mt-3 w-100 waves-effect waves-light btn-rounded btn-outline-primary">
                  View All Alarms
                </button>
              </div>
            </div>
          </div>

          <div className="col-lg-6 my-2">
            <div className="card mb-0 h-100">
              <div className="card-body">
                <h4 className="card-title">AI Recommendations</h4>
                <p className="card-subtitle mb-4">Most recent critical events</p>

                <ul className="feeds ps-0">
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-warning-subtle text-warning btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-bulb fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Reassign 2 idle assets to Team A</span>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 5 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-warning-subtle text-warning btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-bulb fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Add 3 units of Resource Z to meet demand</span>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 12 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-warning-subtle text-warning btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-bulb fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Schedule maintenance for low-use equipment</span>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 25 min ago</span>
                      </div>
                    </div>
                  </div>
                  <div className="feed-item mb-3">
                    <div className="d-md-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <a href="javascript:void(0)" className="btn bg-warning-subtle text-warning btn-circle fs-5 d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="ti ti-bulb fs-6"></i>
                        </a>
                        <div className="ms-3 text-truncate">
                          <span className="text-dark font-weight-medium">Increase server capacity in Region East</span>
                        </div>
                      </div>
                      <div className="justify-content-end text-truncate ms-5 ms-md-auto ps-4 ps-md-0">
                        <span className="fs-2 text-muted">- 1 min ago</span>
                      </div>
                    </div>
                  </div>
                </ul>
                <button type="button" className="btn mb-1 mt-3 w-100 waves-effect waves-light btn-rounded btn-outline-primary">
                  View all recommendations
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    
      
      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );
};

export default DashboardBody;